import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import {ClientMemoryApi} from '../../../api/clientMemoryApi';

/**
 * El estado del knob «¿Cuánto aprueba Dashbo solo?» (card 1EW5v3Ba), en sus dos niveles:
 * `useAgencyMemoryAutonomy()` (el default de la agencia) y `useClientMemoryAutonomy(clientId)`
 * (el override de un cliente, con `null` = seguir a la agencia).
 *
 * Tres decisiones que valen para los dos:
 *
 * 1. **La rama de error es `status !== 'OK'`, no `status === 'ERROR'`.** El backend manda el código
 *    TIPADO en `status` (`FORBIDDEN`, `INVALID_PARAMS`…), así que un `=== 'ERROR'` se los come en
 *    silencio: el control volvería a su lugar y el usuario creería que guardó.
 * 2. **Guardado optimista con rollback.** Son tres radios: esperar el round-trip para pintar la
 *    elección se siente roto. Si el PUT falla, el control vuelve al valor anterior Y avisa — un
 *    rollback mudo es peor que no ser optimista, porque el usuario ya siguió con otra cosa.
 * 3. **Un 404 NO es un error que mostrar**: es «esta agencia no tiene el nivel TOTAL», o un backend
 *    todavía sin deployar. En los dos casos el control simplemente no existe (`available: false`) y
 *    la pantalla que lo monta no dibuja nada. Los repos no deployan a la vez.
 */

/**
 * El aviso de «Listo…» lleva id porque tiene que poder RETIRARSE: cuando el cambio abre la oferta de
 * aprobar la cola, el toast (z 9999) sale en el mismo instante que el modal y le tapa la X —y a 390,
 * también el título— durante sus 5 segundos (QA-VIS-knob2-04, 27/09). El modal ya dice «Acabas de
 * cambiar este ajuste a …», así que cuando aparece, el toast es redundante; cuando no hay oferta, es
 * la única confirmación que existe y se queda.
 */
export const AUTONOMY_SAVED_TOAST_ID = 'memory-autonomy-saved';
const SAVED_TOAST_MS = 5000;

function useAutonomyResource(load, save, {enabled = true} = {}) {
    const {t} = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(enabled);
    const [available, setAvailable] = useState(true);
    const [saving, setSaving] = useState(false);
    const alive = useRef(true);
    const savingRef = useRef(false);
    // Lo último que el backend confirmó. Es a dónde vuelve un rollback.
    const confirmedRef = useRef(null);

    useEffect(() => {
        alive.current = true;
        return () => { alive.current = false; };
    }, []);

    useEffect(() => {
        if (!enabled) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        // Se repone en CADA carga, no sólo en el estado inicial: sin esto, un 404 o un blip de red
        // en el primer fetch apaga el control para SIEMPRE — ni cambiar de cliente lo revive,
        // porque `setData` corre pero `available` sigue en false.
        setAvailable(true);
        load()
            .then((response) => {
                if (cancelled || !alive.current) return;
                if (response && response.status === 'OK' && response.data) {
                    confirmedRef.current = response.data;
                    setData(response.data);
                } else {
                    // 404 (sin nivel TOTAL, o backend viejo) y cualquier otra falla de lectura
                    // caen igual: el control no se ofrece. No hay nada útil que decirle al usuario
                    // sobre una preferencia que su plan no incluye.
                    setAvailable(false);
                }
            })
            .catch(() => { if (!cancelled && alive.current) setAvailable(false); })
            .finally(() => { if (!cancelled && alive.current) setLoading(false); });
        return () => { cancelled = true; };
    }, [load, enabled]);

    // 🔴 Resuelve a un BOOLEANO: si el ajuste quedó guardado de verdad. Lo necesita el aviso de
    // «hay N esperando que se pueden aprobar solos» (card 8uu3IdNd), que sólo puede ofrecerse
    // cuando el nivel nuevo RIGE — con un PUT fallido, la pantalla estaría proponiendo aprobar una
    // tanda por un criterio que no está puesto. Sin esto no había forma de distinguir el éxito del
    // rollback: el `.then` se tragaba las dos ramas y devolvía `undefined`.
    const change = useCallback((value, {successKey} = {}) => {
        // Un guardado por vez. El control ya deshabilita los radios mientras `saving`, pero eso es
        // una defensa de la UI: sin la guarda acá, dos llamadas seguidas dejan dos PUT en vuelo y
        // gana el que conteste último, que no tiene por qué ser el que el usuario eligió último.
        if (savingRef.current) return Promise.resolve(false);
        savingRef.current = true;
        setSaving(true);
        setData((current) => (current ? {...current, ...value.optimistic} : current));
        return save(value.payload)
            .then((response) => {
                if (!alive.current) return false;
                if (response && response.status === 'OK' && response.data) {
                    confirmedRef.current = response.data;
                    setData(response.data);
                    if (successKey) {
                        const message = t(successKey);
                        // ⚠️ Con el id ya activo, react-toastify 11 descarta el aviso EN SILENCIO, y
                        // mover el riel dos veces seguidas es el caso normal: el segundo «Listo…»
                        // simplemente no existiría. `autoClose` va explícito porque `update` no
                        // reinicia el temporizador (5000 = el del `<ToastContainer>` de `App.js`).
                        if (toast.isActive(AUTONOMY_SAVED_TOAST_ID)) {
                            toast.update(AUTONOMY_SAVED_TOAST_ID,
                                {render: message, autoClose: SAVED_TOAST_MS});
                        } else {
                            toast.success(message,
                                {toastId: AUTONOMY_SAVED_TOAST_ID, autoClose: SAVED_TOAST_MS});
                        }
                    }
                    return true;
                }
                setData(confirmedRef.current);
                toast.error(t('memory_autonomy.save_failed'));
                return false;
            })
            .catch(() => {
                if (!alive.current) return false;
                setData(confirmedRef.current);
                toast.error(t('memory_autonomy.save_failed'));
                return false;
            })
            .finally(() => {
                savingRef.current = false;
                if (alive.current) setSaving(false);
            });
    }, [save, t]);

    return {data, loading, available, saving, change};
}

export function useAgencyMemoryAutonomy({enabled = true} = {}) {
    const load = useCallback(() => ClientMemoryApi.getAgencyMemoryAutonomy(), []);
    const save = useCallback((payload) => ClientMemoryApi.setAgencyMemoryAutonomy(payload), []);
    const {data, loading, available, saving, change} = useAutonomyResource(load, save, {enabled});
    return {
        value: data ? data.memory_autonomy : null,
        canEdit: !!(data && data.can_edit),
        loading,
        available: available && !!data,
        saving,
        setValue: (next) => change(
            {payload: next, optimistic: {memory_autonomy: next}},
            {successKey: 'memory_autonomy.saved_agency'}),
    };
}

export function useClientMemoryAutonomy(clientId, {enabled = true} = {}) {
    const load = useCallback(
        () => ClientMemoryApi.getClientMemoryAutonomy(clientId), [clientId]);
    const save = useCallback(
        (payload) => ClientMemoryApi.setClientMemoryAutonomy(clientId, payload), [clientId]);
    const {data, loading, available, saving, change} = useAutonomyResource(
        load, save, {enabled: enabled && !!clientId});
    return {
        // `own` null = hereda del default (oculto) de la agencia; `effective` es lo que rige de
        // verdad. Desde el 08/09 la pantalla muestra SIEMPRE `effective` (card 8uu3IdNd): el knob
        // es por cliente y no hay interruptor de herencia. `own`/`agency`/`inherited` se siguen
        // exponiendo porque el backend los manda y son lo que hay que volver a dibujar el día que
        // el ajuste de agencia se ofrezca de nuevo.
        value: data ? data.own : undefined,
        agencyValue: data ? data.agency : null,
        effective: data ? data.effective : null,
        inherited: !!(data && data.inherited),
        // ¿Este usuario puede activar «Todo de una»? Sólo los admin de la agencia, porque es la
        // única posición que apaga una red de producto (los sensibles dejan de esperar) — decisión
        // de fede del 26/09, hallazgo C-knob-07. **El default con un backend viejo es `true`**: la
        // clave no viaja hasta que se deploye el Backend, y arrancar en `false` le esconderría la
        // posición a los admin que hoy la usan. Quien manda es el backend, que contesta 403; esto
        // sólo existe para no hacer chocar a nadie contra ese 403.
        canSetFull: !data || data.can_set_full !== false,
        loading,
        available: available && !!data,
        saving,
        // El optimismo tiene que mover `effective`, que es lo que se dibuja: con sólo `own`, el
        // riel se quedaba en la posición vieja hasta que contestaba el PUT. `next` es siempre un
        // nivel concreto (mandar `null` = «volvé a heredar» ya no lo ofrece ninguna pantalla), pero
        // si volviera, `effective` no se puede adivinar desde acá y lo repone la respuesta.
        setValue: (next) => change(
            {
                payload: next,
                optimistic: next === null
                    ? {own: null, inherited: true}
                    : {own: next, effective: next, inherited: false},
            },
            {successKey: 'memory_autonomy.saved_client'}),
    };
}
